import {EventSubscriber, HasEventName} from "@/modules/base/events";
import {inject, injectable, multiInject} from "inversify";
import GlobalResolver from "@/modules/base/services/GlobalResolver";
import {BeforeSeasonEvaluationEvent} from "@/modules/season/events/BeforeSeasonEvaluationEvent";
import {Application} from "@/di";
import {Router} from "vue-router";
import {TYPES as STAFF_TYPES} from "@/modules/staff/types";
import {StaffMember} from "@/modules/staff/entity";
import {ConfigRepository} from "@/modules/base/services/ConfigRepository";
import chance from "@/modules/base/services/chance";
import {TYPES} from "@/modules/vue";
import {AbstractStaffMemberFactory} from "@/modules/staff/services/factories/AbstractStaffMemberFactory";

@injectable()
export class SeasonEvaluationSubscriber implements EventSubscriber {
  constructor(
    @inject(Application) private app: Application,
    @inject(GlobalResolver) private globalResolver: GlobalResolver,
    @inject(ConfigRepository) private configRepository: ConfigRepository,
    @multiInject(STAFF_TYPES.StaffMemberFactory) private staffMemberFactories: AbstractStaffMemberFactory[],
  ) {
  }

  priority(): number {
    return 0;
  }

  init(): { event: HasEventName; callback: (event: any) => void }[] {
    return [
      {event: BeforeSeasonEvaluationEvent, callback: this.onBeforeSeasonEvaluationEvent},
    ];
  }

  private async handleCareerEnds() {
    await StaffMember
      .createQueryBuilder('staffMember')
      .update()
      .set({age: () => 'age + 1'})
      .execute();

    const staffMembers = await StaffMember
      .createQueryBuilder('staffMember')
      .leftJoinAndSelect('staffMember.team', 'team')
      .getMany();

    const endCareerStaffMembers = await staffMembers
      .filter(m => this.configRepository.get('staff.willEndCareer')(m))
      .forEachAsync(async (m: StaffMember) => {
        m.endsCareer = true;
        await m.save();
      });

    if (endCareerStaffMembers.length === 0) {
      return;
    }

    await this.globalResolver.startPromise(() => {
      this.app.get<Router>(TYPES.VueRouter).push({name: 'staff.career-ended'});
    });

    await (
      await StaffMember
        .createQueryBuilder('staffMember')
        .where('staffMember.endsCareer = true')
        .getMany()
    )
      .forEachAsync(async (m: StaffMember) => {
        await m.remove();
        await chance.pickone(this.staffMemberFactories).generate();
      });
  }

  async onBeforeSeasonEvaluationEvent(event: BeforeSeasonEvaluationEvent): Promise<void> {
    await this.handleCareerEnds();

    await this.globalResolver.startPromise(() => {
      this.app.get<Router>(TYPES.VueRouter).push({name: 'staff.team.sack'});
    });
  }
}